import { signedDistanceInDirectionOf } from '@geometry'
import { object, number, mixed } from 'yup'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { SPLIT_SAFETY_TOLERANCE } from './utils'

const pointSchema = object({
  x: number().required('X coordinate is required').defined(),
  y: number().required('Y coordinate is required').defined(),
  z: number().required('Z coordinate is required').defined(),
})

/**
 * Calculates the position of an opening along the wall axis
 */
const getOpeningPositionAlongAxis = (
  wallStart: ImmutableVector3,
  wallAxisDirection: ImmutableVector3,
  openingPoint: Point,
): number => {
  const openingVec = new ImmutableVector3(openingPoint.x, openingPoint.y, openingPoint.z)
  return signedDistanceInDirectionOf(wallStart, openingVec, wallAxisDirection)
}

export const splitWallSchema = object({
  splitPoint: pointSchema.test({
    name: 'split-point-within-bounds',
    message:
      'Projektion des Trennungspunktes muss zwischen P0 und P1 liegen und darf nicht durch eine Öffnung gehen',
    test: function (splitPoint) {
      const { wall } = this.parent

      if (!splitPoint || !wall?.shape?.points || wall.shape.points.length < 2) {
        return true // Skip validation if data is not complete
      }

      const points = wall.shape.points
      const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
      const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
      const split = new ImmutableVector3(splitPoint.x, splitPoint.y, splitPoint.z)

      // Calculate wall axis
      const wallAxis = p1.sub(p0)
      const wallAxisDirection = wallAxis.normalize()
      const wallAxisLength = p0.distanceTo(p1)

      // Calculate signed distance from p0 to splitPoint along wall axis
      const distanceP0ToSplitPointAlongAxis = signedDistanceInDirectionOf(
        p0,
        split,
        wallAxisDirection,
      )

      // Check if split point is within bounds [0, wallAxisLength]
      // Also ensure it's not too close to the endpoints (minimum wall length)
      const minWallLength = 0.1
      if (
        distanceP0ToSplitPointAlongAxis < minWallLength ||
        distanceP0ToSplitPointAlongAxis > wallAxisLength - minWallLength
      ) {
        return false
      }

      // Check that split point doesn't go through any openings
      if (wall.openings) {
        const nonLocalOpenings = wall.openings.filter((opening: Opening) => !opening.is_local)

        for (const opening of nonLocalOpenings) {
          const openingStart = opening.shape.points[0]
          const openingEnd = opening.shape.points[1]

          // Calculate opening positions along the wall axis
          const openingStartPos = getOpeningPositionAlongAxis(p0, wallAxisDirection, openingStart)
          const openingEndPos = getOpeningPositionAlongAxis(p0, wallAxisDirection, openingEnd)

          const openingMinPos = Math.min(openingStartPos, openingEndPos)
          const openingMaxPos = Math.max(openingStartPos, openingEndPos)

          // Check if split point is within opening range (with safety tolerance)
          if (
            distanceP0ToSplitPointAlongAxis >= openingMinPos - SPLIT_SAFETY_TOLERANCE &&
            distanceP0ToSplitPointAlongAxis <= openingMaxPos + SPLIT_SAFETY_TOLERANCE
          ) {
            return false
          }
        }
      }

      return true
    },
  }),
  wall: mixed<ShapeObject>(),
})
